import Config from 'react-native-config';
import {
  getKioskDeviceIdentifier,
  getKioskId,
  hasActivatedKioskSession,
} from '../services/kioskTokenStore';

function positiveIntegerOrDefault(
  name: string,
  value: string | undefined,
  fallback: number,
): number {
  if (value == null || String(value).trim() === '') return fallback;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new Error(`${name} must be a positive integer`);
  }
  return parsed;
}

/** True when this build runs as a walk-up office kiosk (no employee login by default). */
export function isKioskMode(): boolean {
  const mode = String(Config.APP_MODE || '')
    .trim()
    .toLowerCase();
  return mode === 'kiosk';
}

export function getKioskIdleResetMs(): number {
  const idleResetMs = positiveIntegerOrDefault(
    'KIOSK_IDLE_RESET_MS',
    Config.KIOSK_IDLE_RESET_MS,
    4000,
  );
  if (idleResetMs < 1500) {
    throw new Error('KIOSK_IDLE_RESET_MS must be at least 1500');
  }
  return idleResetMs;
}

/**
 * Runtime device fields for face-punch.
 * Requires a successfully activated kiosk session (no env secrets / static IDs).
 */
export function getKioskDeviceConfig() {
  if (!hasActivatedKioskSession()) {
    throw Object.assign(
      new Error('Kiosk is not activated on this device'),
      { code: 'KIOSK_NOT_ACTIVATED' },
    );
  }
  const kioskId = getKioskId();
  const deviceIdentifier = getKioskDeviceIdentifier();
  if (!kioskId || !deviceIdentifier) {
    throw Object.assign(
      new Error('Kiosk is not activated on this device'),
      { code: 'KIOSK_NOT_ACTIVATED' },
    );
  }

  return {
    kioskId,
    deviceIdentifier,
    idleResetMs: getKioskIdleResetMs(),
    captureSource: 'KIOSK' as const,
  };
}

/** @deprecated Env credential auth removed — activation JWT only. */
export type KioskAuthConfig = {
  loginName: string | null;
  password: string | null;
  accessToken: string | null;
};

/** @deprecated Env credential auth removed — activation JWT only. */
export function getKioskAuthConfig(): KioskAuthConfig {
  return { loginName: null, password: null, accessToken: null };
}

/** @deprecated Use getKioskDeviceConfig after activation. */
export function getKioskConfig() {
  return {
    ...getKioskDeviceConfig(),
    accessToken: '',
    loginName: null,
    password: null,
  };
}
